import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import {
    getIsOrderSubmissionLocked,
    getSelectedOrder,
    getSelectedOrderId,
} from 'entities/Order/model/selectors/ordersSelectors';
import { getProposalQuantities } from 'entities/Proposal/lib/getProposalQuantities';
import { UserRole, getUserRole } from 'entities/User';
import { getUserCanEditPropertyCatalog } from 'entities/User/model/selectors/userPermissionsSeletors';
import { getRouteOrdersViewItemDetailed } from 'shared/const/router';
import { formatNumber } from 'shared/lib/formatNumber/formatNumber';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { OrderPriceDisplay } from 'shared/ui/OrderPriceDisplay/OrderPriceDisplay';
import { SuppliersForItem } from 'shared/ui/SuppliersForItem';
import { useOrderItemHooks } from 'widgets/OrderGroupedItems/hooks/OrderItemHooks';

import { OrderGroupedItemsTableRowActions } from './OrderGroupedItemsTableRowActions';
import {
    baseSupplierButtonSxProps,
    getBaseSupplierButtonClickableSxProps,
} from './OrderGroupedItemsTableRowSuppliers';

interface OrderGroupItemsTableRowSubstitutionsProps {
    item: CatalogItem;
}

export const OrderGroupItemsTableRowSubstitutions = ({
    item,
}: OrderGroupItemsTableRowSubstitutionsProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const selectedOrderId = useSelector(getSelectedOrderId);
    const selectedOrder = useSelector(getSelectedOrder);
    const canEditCatalog = useSelector(getUserCanEditPropertyCatalog);

    const userRole = useSelector(getUserRole);
    const {
        allSuppliersSubstituted,
        someSuppliersSubstituted,
        substitutedProposals,
    } = useOrderItemHooks(item);

    const hasSubstitutions =
        someSuppliersSubstituted || allSuppliersSubstituted;

    const canViewProposals = ![UserRole.MAINTENANCE_TECHNICIAN].includes(
        userRole,
    );
    const isOrderSubmissionLocked = useSelector(getIsOrderSubmissionLocked);

    const openItem = () => {
        if (!canViewProposals || isOrderSubmissionLocked) return;
        navigate(getRouteOrdersViewItemDetailed(selectedOrderId, item.id));
        dispatch(catalogItemActions.setSelectedItem(item));
    };

    if (!hasSubstitutions || substitutedProposals.length === 0) {
        return null;
    }

    return (
        <Box sx={{ paddingTop: '20px', paddingBottom: '17px' }}>
            <Typography
                sx={{
                    color: '#000',
                    fontSize: '12px',
                    fontWeight: 500,
                    lineHeight: '20px',
                    marginBottom: '10px',
                }}
            >
                {substitutedProposals.length > 1
                    ? t('Substitutions')
                    : t('Substitution')}
            </Typography>

            {substitutedProposals.map((proposal, index) => {
                const { approvedQty, unavailableQty } =
                    getProposalQuantities(proposal);

                const totalPrice = proposal.price * approvedQty;

                return (
                    <Box
                        key={proposal.uid}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            borderTop:
                                index > 0
                                    ? '1px solid rgba(0, 0, 0, 0.15)'
                                    : 'none',
                            paddingTop: index > 0 ? '16px' : '0px',
                            marginTop: index > 0 ? '16px' : '0px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexGrow: 1,
                                }}
                            >
                                <Typography
                                    typography={'openSans.subtitle1Medium'}
                                    data-testclass="order-item-substitution-title"
                                    onClick={openItem}
                                    sx={{
                                        fontSize: '15px',
                                        fontWeight: 600,
                                        letterSpacing: '0',
                                        display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: '3',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        cursor: canViewProposals
                                            ? 'pointer'
                                            : 'default',
                                    }}
                                >
                                    {proposal.replacedWith}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    width: '50px',
                                    minWidth: '50px',
                                    display: 'flex',
                                    justifyContent: 'right',
                                    marginLeft: '40px',
                                    flexGrow: 0,
                                }}
                            >
                                <Typography
                                    typography="openSans.body2"
                                    data-testclass="order-item-substitution-qty"
                                >
                                    {formatNumber(approvedQty)}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    width: '100px',
                                    minWidth: '100px',
                                    display: 'flex',
                                    justifyContent: 'right',
                                    flexGrow: 0,
                                }}
                            >
                                <OrderPriceDisplay
                                    width="60px"
                                    containerHeight="32px"
                                    containerProps={{ fontWeight: 600 }}
                                    typography="openSans.body"
                                    testClass="order-item-substitution-total-price"
                                    price={totalPrice}
                                    orderHasLoadingPrices={false}
                                    order={selectedOrder}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ paddingTop: '10px' }}>
                            <Box
                                sx={{
                                    marginTop: '-7px',
                                    marginLeft: '-7px',
                                    ...baseSupplierButtonSxProps,
                                    ...getBaseSupplierButtonClickableSxProps(
                                        canViewProposals,
                                    ),
                                }}
                                onClick={openItem}
                            >
                                <SuppliersForItem
                                    selectedProposals={[proposal]}
                                    showEmptyProposals={false}
                                    subtitle={
                                        unavailableQty > 0
                                            ? `${unavailableQty} ${t(
                                                  'unavailable',
                                              )}`
                                            : undefined
                                    }
                                />
                            </Box>
                        </Box>
                    </Box>
                );
            })}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                }}
            >
                <OrderGroupedItemsTableRowActions
                    itemId={item.id}
                    catalogItemId={item.catalogItemId}
                    itemName={item.title}
                    categoryId={item.categoryId}
                    canEditCatalog={canEditCatalog}
                    canDelete={false}
                    isOrderSubmissionLocked={isOrderSubmissionLocked}
                />
            </Box>
        </Box>
    );
};
